import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../appStore";

// Define the types for General Info form data
interface PlumbingFormData {
  PlumberName?: string;
  PlumberEmail?: string;
  PlumberPhoneNumber?: string;
  UtilityCompany?: string;
  FederalID?: string;
  ContractorLicenseNumber?: string;
  PlumberLicenseExpiryDate?: string;
  DescriptionOfWork?: string;
  PlumbingDocumentFile?: File[] | null;
  [key: string]: any; // Allow for additional fields
}

// Define work item interface
interface WorkItem {
  id: string;
  name: string;
  quantity: number;
  apiName: string; // Field to map to API parameter names
  section?: 'basic' | 'additional'; // Optional field to categorize for UI display
}

// Define plumbing characteristics interface
interface PlumbingCharacteristics {
  buildingSewerSize: string;
  sewerType: 'Public Sewer' | 'Private Septic' | '';
  waterServiceSize: string;
  waterType: 'Public Water' | 'Private Well' | '';
}

// Define fee summary item interface for API response
interface FeeSummaryItem {
  Item: string;
  Fee: number;
}

// Define fees response interface
interface FeesResponse {
  totalFee: number;
  summary: FeeSummaryItem[];
}

// Define the state interface
interface PlumbingSubcodeState {
  formData: PlumbingFormData | null;
  workItems: WorkItem[];
  plumbingCharacteristics: PlumbingCharacteristics | null;
  fees: FeesResponse | null;
}

// Define initial state
const initialState: PlumbingSubcodeState = {
  formData: null,
  workItems: [],
  plumbingCharacteristics: null,
  fees: null
};

const plumbingSubcodeSlice = createSlice({
  name: 'plumbingSubcode',
  initialState,
  reducers: {
    // Update form data with new fields
    updatePlumbingFormData: (state, action: PayloadAction<PlumbingFormData>) => {
      state.formData = {
        ...state.formData,
        ...action.payload
      };
    },
    
    // Set all work items at once
    setWorkItems: (state, action: PayloadAction<WorkItem[]>) => {
      state.workItems = action.payload;
    },
    
    // Add multiple work items
    addWorkItems: (state, action: PayloadAction<WorkItem[]>) => {
      // For each new item, either add it or update if it already exists
      action.payload.forEach(newItem => {
        const existingIndex = state.workItems.findIndex(item => item.id === newItem.id);
        if (existingIndex !== -1) {
          state.workItems[existingIndex] = newItem;
        } else {
          state.workItems.push(newItem);
        }
      });
    },
    
    // Update a single work item quantity
    updateWorkItemQuantity: (state, action: PayloadAction<{
      id: string;
      quantity: number;
    }>) => {
      const { id, quantity } = action.payload;
      const itemIndex = state.workItems.findIndex(item => item.id === id);
      
      if (itemIndex !== -1) {
        state.workItems[itemIndex].quantity = quantity;
      }
    },
    
    // Update plumbing characteristics
    updatePlumbingCharacteristics: (state, action: PayloadAction<PlumbingCharacteristics>) => {
      state.plumbingCharacteristics = {
        ...state.plumbingCharacteristics,
        ...action.payload
      };
    },
    
    // Update a single plumbing characteristic field
    updatePlumbingCharacteristic: (state, action: PayloadAction<{
      field: keyof PlumbingCharacteristics;
      value: string;
    }>) => {
      const { field, value } = action.payload;
      
      if (!state.plumbingCharacteristics) {
        state.plumbingCharacteristics = {
          buildingSewerSize: '',
          sewerType: '',
          waterServiceSize: '',
          waterType: ''
        };
      }
      
      state.plumbingCharacteristics[field] = value as any;
    },
    
    // Update fees
    updateFees: (state, action: PayloadAction<FeesResponse>) => {
      state.fees = action.payload;
    },
    
    // Set entire form data object (useful for prefilling or resetting)
    setPlumbingFormData: (state, action: PayloadAction<PlumbingFormData | null>) => {
      state.formData = action.payload;
    },
    
    // Clear form data (reset to initial state)
    clearPlumbingFormData: (state) => {
      state.formData = null;
    },
    
    // Clear everything (reset to initial state)
    clearAllPlumbingData: () => initialState
  }
});

export const {
  updatePlumbingFormData,
  setWorkItems,
  addWorkItems,
  updateWorkItemQuantity,
  updatePlumbingCharacteristics,
  updatePlumbingCharacteristic,
  updateFees,
  setPlumbingFormData,
  clearPlumbingFormData,
  clearAllPlumbingData
} = plumbingSubcodeSlice.actions;

export default plumbingSubcodeSlice.reducer;

// Selectors
export const selectPlumbingFormData = (state: RootState) => state.plumbingSubcode.formData;
export const selectAllWorkItems = (state: RootState) => state.plumbingSubcode.workItems;
export const selectPlumbingCharacteristics = (state: RootState) => state.plumbingSubcode.plumbingCharacteristics;
export const selectFees = (state: RootState) => state.plumbingSubcode.fees;

// Helper selectors for UI display
export const selectBasicFixtures = (state: RootState) => 
  state.plumbingSubcode.workItems.filter(item => item.section === 'basic' || !item.section);

export const selectAdditionalComponents = (state: RootState) => 
  state.plumbingSubcode.workItems.filter(item => item.section === 'additional');

// Helper selector to get all work items with quantity > 0
export const selectItemsWithQuantity = (state: RootState) => 
  state.plumbingSubcode.workItems.filter(item => item.quantity > 0);